import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ticketApi } from './api';
import type { Ticket, TicketStatus } from './api';
import StatusBadge from './StatusBadge';
import SlaPill from './SlaPill';

const NEXT_STATUS: TicketStatus[] = ['OPEN', 'ACKNOWLEDGED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'CANCELLED'];

function getLoggedInEmail() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.email || '';
  } catch {
    return '';
  }
}

export default function TicketDetail() {
  const { id } = useParams();
  const loggedInEmail = getLoggedInEmail();

  const isSuperAdmin = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      return user?.role?.toLowerCase() === 'super_admin';
    } catch {
      return false;
    }
  })();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusForm, setStatusForm] = useState({ status: '', note: '', changedBy: loggedInEmail, assignedTo: '' });
  const [commentForm, setCommentForm] = useState({ author: loggedInEmail, body: '' });

  async function loadTicket() {
    if (!id) return;
    try {
      setTicket(await ticketApi.getTicket(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load ticket');
    }
  }

  useEffect(() => {
    loadTicket();
  }, [id]);

  async function submitStatus(event: FormEvent) {
    event.preventDefault();
    if (!id || !ticket) return;
    try {
      await ticketApi.updateStatus(id, {
        status: statusForm.status || ticket.status,
        note: statusForm.note,
        changedBy: loggedInEmail,
        ...(isSuperAdmin && statusForm.assignedTo.trim()
          ? { assignedTo: statusForm.assignedTo.trim() }
          : {}),
      });
      setStatusForm({ status: '', note: '', changedBy: getLoggedInEmail() || statusForm.changedBy, assignedTo: '' });
      loadTicket();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update status');
    }
  }

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    if (!id) return;
    try {
      await ticketApi.addComment(id, commentForm);
      setCommentForm({ author: getLoggedInEmail() || commentForm.author, body: '' });
      loadTicket();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to post message');
    }
  }

  if (error) return <div className="ticket-alert error">{error}</div>;
  if (!ticket) return <p className="ticket-muted">Loading...</p>;

  return (
    <div className="ticket-detail">
      <Link to="/tickets/track" className="ticket-back">Back to tickets</Link>

      <div className="ticket-detail-hero">
        <div className="ticket-card ticket-summary-card">
          <div className="ticket-row between">
            <div>
              <h2>{ticket.reference}</h2>
              <p className="ticket-muted">{ticket.category}</p>
            </div>
            <div className="ticket-row gap center-v">
              <StatusBadge status={ticket.status} />
              <SlaPill sla={ticket.sla} />
            </div>
          </div>
          <h3>{ticket.subject}</h3>
          <p>{ticket.description}</p>
          <dl className="ticket-meta">
            <div><dt>Raised by</dt><dd>{ticket.raisedBy}</dd></div>
            <div><dt>Priority</dt><dd>{ticket.priority}</dd></div>
            <div><dt>Assigned to</dt><dd>{ticket.assignedTo || '-'}</dd></div>
            <div>
              <dt>Asset / Product / Campaign</dt>
              <dd>
                {ticket.assetId || ticket.productOrCampaign || '-'}
              </dd>
            </div>
            <div><dt>Created</dt><dd>{new Date(ticket.createdAt).toLocaleString()}</dd></div>
            <div><dt>Response due</dt><dd>{new Date(ticket.responseDueAt).toLocaleString()}</dd></div>
            <div><dt>Resolution due</dt><dd>{new Date(ticket.resolutionDueAt).toLocaleString()}</dd></div>
          </dl>
        </div>

        <aside className="ticket-card ticket-history-card">
          <div className="ticket-history-head">
            <h3>Status history</h3>
            <span>{ticket.history?.length || 0}</span>
          </div>
          <ul className="ticket-timeline compact">
            {ticket.history?.length ? ticket.history.map((history) => (
              <li key={history.id}>
                <span className="ticket-timeline-dot" />
                <div>
                  <strong>{history.to_status.replace('_', ' ')}</strong>
                  <div className="ticket-muted">{new Date(history.changed_at).toLocaleString()}</div>
                  {history.from_status && <div className="ticket-muted">{history.from_status.replace('_', ' ')} to {history.to_status.replace('_', ' ')}</div>}
                  {history.note && <p>{history.note}</p>}
                  {history.changed_by && <span className="ticket-muted">by {history.changed_by}</span>}
                </div>
              </li>
            )) : (
              <li>
                <span className="ticket-timeline-dot" />
                <div className="ticket-muted">No status history yet.</div>
              </li>
            )}
          </ul>
        </aside>
      </div>

      <div className="ticket-dash-grid">
        <div className="ticket-card">
          <h3>Update status</h3>
          <form onSubmit={submitStatus} className="ticket-form">
            <label>New status
              <select value={statusForm.status} onChange={(event) => setStatusForm({ ...statusForm, status: event.target.value })}>
                <option value="">{`Keep (${ticket.status})`}</option>
                {NEXT_STATUS.map((status) => <option key={status} value={status}>{status.replace('_', ' ')}</option>)}
              </select>
            </label>
            {isSuperAdmin && (
              <label>
                Assign to
                <input
                  type="email"
                  value={statusForm.assignedTo}
                  onChange={(event) =>
                    setStatusForm({
                      ...statusForm,
                      assignedTo: event.target.value,
                    })
                  }
                  placeholder="Enter a registered SIMB user email"
                />
              </label>
            )}
            <label>Note
              <textarea rows={2} value={statusForm.note} onChange={(event) => setStatusForm({ ...statusForm, note: event.target.value })} />
            </label>
            <label>Your name/email
              <input value={statusForm.changedBy} onChange={(event) => setStatusForm({ ...statusForm, changedBy: event.target.value })} />
            </label>
            <button type="submit">Apply update</button>
          </form>
        </div>

        <div className="ticket-card">
          <h3>Communications</h3>
          <div className="ticket-comments">
            {(!ticket.comments || ticket.comments.length === 0) && <p className="ticket-muted">No messages yet.</p>}
            {ticket.comments?.map((comment) => (
              <div key={comment.id} className={`ticket-comment ${comment.is_internal ? 'internal' : ''}`}>
                <div className="ticket-comment-head">
                  <strong>{comment.author}</strong>
                  {comment.is_internal && <span className="ticket-tag">internal</span>}
                  <span className="ticket-muted">{new Date(comment.created_at).toLocaleString()}</span>
                </div>
                <p>{comment.body}</p>
              </div>
            ))}
          </div>
          <form onSubmit={submitComment} className="ticket-form">
            <label>Your name/email
              <input value={commentForm.author} onChange={(event) => setCommentForm({ ...commentForm, author: event.target.value })} required />
            </label>
            <label>Message
              <textarea rows={2} value={commentForm.body} onChange={(event) => setCommentForm({ ...commentForm, body: event.target.value })} required />
            </label>
            <button type="submit">Post message</button>
          </form>
        </div>
      </div>
    </div>
  );
}
